import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Check, X, Loader2, MapPin, CreditCard, FileText, Truck, Building2,
  User, ChevronDown, Search
} from "lucide-react";
import { API } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import SHOP from "@/config/shop";

const Field = ({ label, children, required }) => (
  <div>
    <label className="text-xs font-bold uppercase text-slate-500 mb-1 block">{label}{required && ' *'}</label>
    {children}
  </div>
);
const inputCls = "w-full h-11 border border-slate-300 rounded px-4 text-sm focus:border-[#004E98] focus:ring-1 focus:ring-[#004E98] outline-none";

export default function CheckoutPage() {
  const { items, total, clearCart } = useCart();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState('');
  const [billing, setBilling] = useState({
    firstname: '', lastname: '', company: '', ico: '', dic: '',
    address1: '', city: '', postcode: '', phone: '', email: ''
  });
  const [differentShipping, setDifferentShipping] = useState(false);
  const [shipping, setShipping] = useState({
    firstname: '', lastname: '', company: '',
    address1: '', city: '', postcode: '', phone: '', email: ''
  });
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [coupon, setCoupon] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [aresLoading, setAresLoading] = useState(false);
  useSEO('Pokladna', `Dokonceni objednavky na ${SHOP.domain}`);

  const shippingCost = total >= 2000 ? 0 : 99;
  const codFee = paymentMethod === 'cod' ? 39 : 0;
  const discount = couponApplied?.discount || 0;
  const grandTotal = total - discount + shippingCost + codFee;

  useEffect(() => {
    if (user && token) {
      setBilling(b => ({ ...b, firstname: user.firstname || '', lastname: user.lastname || '', email: user.email || '', company: user.company || '' }));
      axios.get(`${API}/user/addresses`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => {
          setAddresses(res.data);
          const def = res.data.find(a => a.is_default);
          if (def) applyAddress(def);
        }).catch(() => {});
    }
  }, [user, token]);

  const applyAddress = (addr) => {
    setBilling(b => ({
      ...b,
      firstname: addr.firstname || b.firstname,
      lastname: addr.lastname || b.lastname,
      company: addr.company || '',
      address1: addr.address1 || '',
      city: addr.city || '',
      postcode: addr.postcode || '',
      phone: addr.phone || '',
    }));
  };

  const handleAddressSelect = (e) => {
    const id = e.target.value;
    setSelectedAddress(id);
    if (id) {
      const addr = addresses.find(a => a.id === id);
      if (addr) applyAddress(addr);
    }
  };

  // ARES lookup
  const lookupARES = async () => {
    const ico = billing.ico?.trim();
    if (!ico || ico.length < 2) { toast.error('Zadejte ICO'); return; }
    setAresLoading(true);
    try {
      const res = await axios.get(`${API}/ares/${ico}`);
      setBilling(b => ({
        ...b,
        company: res.data.company || b.company,
        dic: res.data.dic || b.dic,
        address1: res.data.address1 || b.address1,
        city: res.data.city || b.city,
        postcode: res.data.postcode || b.postcode,
      }));
      toast.success(`Nalezeno: ${res.data.company}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Subjekt nenalezen v ARES');
    } finally { setAresLoading(false); }
  };

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    try {
      const res = await axios.post(`${API}/coupons/validate`, {
        code: coupon.trim(), subtotal: total, cart_items: items.map(i => ({ id_product: i.id_product, quantity: i.quantity }))
      }, token ? { headers: { Authorization: `Bearer ${token}` } } : {});
      setCouponApplied(res.data);
      toast.success('Slevovy kod pouzit');
    } catch (err) { toast.error(err.response?.data?.detail || 'Neplatny kod'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!billing.firstname || !billing.lastname || !billing.email || !billing.address1 || !billing.city || !billing.postcode || !billing.phone) {
      toast.error('Vyplnte vsechna povinná pole'); return;
    }
    if (items.length === 0) { toast.error('Kosik je prazdny'); return; }
    setSubmitting(true);
    try {
      const orderData = {
        billing_address: billing,
        shipping_address: differentShipping ? shipping : null,
        shipping_same_as_billing: !differentShipping,
        payment_method: paymentMethod,
        note,
        cart_items: items.map(i => ({ id_product: i.id_product, quantity: i.quantity })),
        coupon_code: couponApplied ? coupon : null,
      };
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(`${API}/orders`, orderData, { headers });

      clearCart();
      if (paymentMethod === 'card' && res.data.id) {
        const payRes = await axios.post(`${API}/payments/create-checkout`, {
          order_id: res.data.id, origin_url: window.location.origin
        }, { headers });
        if (payRes.data.url) { window.location.href = payRes.data.url; return; }
      }
      navigate(`/platba-uspesna?order=${res.data.order_number}`);
      toast.success('Objednavka vytvorena!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Chyba pri vytvareni objednavky'); }
    finally { setSubmitting(false); }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-bold text-slate-800 mb-4">Kosik je prazdny</h1>
        <Link to="/produkty" className="text-[#004E98] hover:underline">Zpet na produkty</Link>
      </div>
    );
  }

  const PAYMENTS = [
    { id: 'card', label: 'Platba kartou', desc: 'Visa, Mastercard', icon: CreditCard, color: 'text-blue-600' },
    { id: 'invoice', label: 'Faktura (prevod)', desc: 'Platba prevodem', icon: FileText, color: 'text-emerald-600' },
    { id: 'cod', label: 'Dobirka', desc: `+${39} Kc`, icon: Truck, color: 'text-amber-600' },
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 sm:py-10" data-testid="checkout-page">
      <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-6">Pokladna</h1>

      {!user && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-center justify-between">
          <p className="text-sm text-blue-700">Mate ucet? <Link to="/prihlaseni" className="font-medium underline">Prihlaste se</Link> pro rychlejsi nakup.</p>
          <span className="text-xs text-blue-500">Nebo pokracujte bez registrace</span>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Billing address */}
            <div className="bg-white border border-slate-200 rounded-lg p-6">
              <h2 className="font-semibold text-slate-800 mb-4 flex items-center gap-2"><User className="w-5 h-5" /> Fakturacni udaje</h2>

              {addresses.length > 0 && (
                <div className="mb-4">
                  <Field label="Ulozene adresy">
                    <select value={selectedAddress} onChange={handleAddressSelect} className={inputCls} data-testid="saved-address-select">
                      <option value="">-- Vyberte ulozenou adresu --</option>
                      {addresses.map(a => <option key={a.id} value={a.id}>{a.label}: {a.firstname} {a.lastname}, {a.city}</option>)}
                    </select>
                  </Field>
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Jmeno" required><input type="text" value={billing.firstname} onChange={e => setBilling({...billing, firstname: e.target.value})} className={inputCls} data-testid="billing-firstname" required /></Field>
                <Field label="Prijmeni" required><input type="text" value={billing.lastname} onChange={e => setBilling({...billing, lastname: e.target.value})} className={inputCls} data-testid="billing-lastname" required /></Field>
                <Field label="Email" required><input type="email" value={billing.email} onChange={e => setBilling({...billing, email: e.target.value})} className={inputCls} data-testid="billing-email" required /></Field>
                <Field label="Telefon" required><input type="tel" value={billing.phone} onChange={e => setBilling({...billing, phone: e.target.value})} className={inputCls} data-testid="billing-phone" required /></Field>
              </div>

              {/* Company / ARES */}
              <div className="mt-4 pt-4 border-t border-slate-100">
                <h3 className="text-xs font-bold uppercase text-slate-500 mb-3 flex items-center gap-1"><Building2 className="w-4 h-4" /> Firemni udaje (volitelne)</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Field label="ICO">
                      <div className="flex gap-2">
                        <input type="text" value={billing.ico || ''} onChange={e => setBilling({...billing, ico: e.target.value})} placeholder="12345678" className={inputCls} data-testid="billing-ico" />
                        <button type="button" onClick={lookupARES} disabled={aresLoading}
                          className="h-11 px-4 bg-[#004E98] text-white rounded text-sm font-medium whitespace-nowrap flex items-center gap-1 hover:bg-[#003B73] disabled:opacity-50" data-testid="ares-lookup-btn">
                          {aresLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} ARES
                        </button>
                      </div>
                    </Field>
                  </div>
                  <Field label="DIC"><input type="text" value={billing.dic || ''} onChange={e => setBilling({...billing, dic: e.target.value})} placeholder="CZ12345678" className={inputCls} data-testid="billing-dic" /></Field>
                  <div className="sm:col-span-2"><Field label="Nazev firmy"><input type="text" value={billing.company || ''} onChange={e => setBilling({...billing, company: e.target.value})} className={inputCls} data-testid="billing-company" /></Field></div>
                </div>
              </div>

              {/* Address */}
              <div className="mt-4 pt-4 border-t border-slate-100">
                <h3 className="text-xs font-bold uppercase text-slate-500 mb-3 flex items-center gap-1"><MapPin className="w-4 h-4" /> Adresa</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2"><Field label="Ulice a cislo" required><input type="text" value={billing.address1} onChange={e => setBilling({...billing, address1: e.target.value})} className={inputCls} data-testid="billing-address" required /></Field></div>
                  <Field label="Mesto" required><input type="text" value={billing.city} onChange={e => setBilling({...billing, city: e.target.value})} className={inputCls} data-testid="billing-city" required /></Field>
                  <Field label="PSC" required><input type="text" value={billing.postcode} onChange={e => setBilling({...billing, postcode: e.target.value})} className={inputCls} data-testid="billing-postcode" required /></Field>
                </div>
              </div>
            </div>

            {/* Shipping address */}
            <div className="bg-white border border-slate-200 rounded-lg p-6">
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={differentShipping} onChange={e => setDifferentShipping(e.target.checked)} className="w-4 h-4 text-[#004E98] rounded" data-testid="different-shipping-checkbox" />
                <span className="font-semibold text-slate-800 flex items-center gap-2"><Truck className="w-5 h-5" /> Dorucit na jinou adresu</span>
              </label>
              {differentShipping && (
                <div className="grid sm:grid-cols-2 gap-4 mt-4 pt-4 border-t border-slate-100">
                  <Field label="Jmeno" required><input type="text" value={shipping.firstname} onChange={e => setShipping({...shipping, firstname: e.target.value})} className={inputCls} data-testid="shipping-firstname" required /></Field>
                  <Field label="Prijmeni" required><input type="text" value={shipping.lastname} onChange={e => setShipping({...shipping, lastname: e.target.value})} className={inputCls} data-testid="shipping-lastname" required /></Field>
                  <Field label="Telefon"><input type="tel" value={shipping.phone || ''} onChange={e => setShipping({...shipping, phone: e.target.value})} className={inputCls} data-testid="shipping-phone" /></Field>
                  <Field label="Firma"><input type="text" value={shipping.company || ''} onChange={e => setShipping({...shipping, company: e.target.value})} className={inputCls} /></Field>
                  <div className="sm:col-span-2"><Field label="Ulice a cislo" required><input type="text" value={shipping.address1} onChange={e => setShipping({...shipping, address1: e.target.value})} className={inputCls} data-testid="shipping-address" required /></Field></div>
                  <Field label="Mesto" required><input type="text" value={shipping.city} onChange={e => setShipping({...shipping, city: e.target.value})} className={inputCls} data-testid="shipping-city" required /></Field>
                  <Field label="PSC" required><input type="text" value={shipping.postcode} onChange={e => setShipping({...shipping, postcode: e.target.value})} className={inputCls} data-testid="shipping-postcode" required /></Field>
                </div>
              )}
            </div>

            {/* Payment method */}
            <div className="bg-white border border-slate-200 rounded-lg p-6">
              <h2 className="font-semibold text-slate-800 mb-4 flex items-center gap-2"><CreditCard className="w-5 h-5" /> Zpusob platby</h2>
              <div className="grid sm:grid-cols-3 gap-3">
                {PAYMENTS.map(p => (
                  <button key={p.id} type="button" onClick={() => setPaymentMethod(p.id)}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${paymentMethod === p.id ? 'border-[#004E98] bg-blue-50/50' : 'border-slate-200 hover:border-slate-300'}`}
                    data-testid={`payment-${p.id}`}>
                    <p.icon className={`w-6 h-6 mb-2 ${p.color}`} />
                    <p className="font-medium text-sm text-slate-800">{p.label}</p>
                    <p className="text-xs text-slate-500">{p.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Note */}
            <div className="bg-white border border-slate-200 rounded-lg p-6">
              <Field label="Poznamka k objednavce">
                <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} placeholder="Specialni pozadavky, cas doruceni..."
                  className="w-full border border-slate-300 rounded p-3 text-sm focus:border-[#004E98] focus:ring-1 focus:ring-[#004E98] outline-none" data-testid="order-note" />
              </Field>
            </div>
          </div>

          {/* Order summary sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-slate-200 rounded-lg p-6 sticky top-4">
              <h2 className="font-semibold text-slate-800 mb-4">Souhrn objednavky</h2>

              <div className="space-y-3 mb-4">
                {items.map(item => (
                  <div key={item.id_product} className="flex justify-between text-sm">
                    <div className="flex-1 pr-2">
                      <p className="font-medium text-slate-700 line-clamp-1">{item.name}</p>
                      <p className="text-xs text-slate-400">{item.quantity}x {item.price_vat?.toFixed(2)} Kc</p>
                    </div>
                    <span className="font-medium whitespace-nowrap">{(item.price_vat * item.quantity).toFixed(2)} Kc</span>
                  </div>
                ))}
              </div>

              {/* Coupon */}
              <div className="flex gap-2 mb-4">
                <input type="text" value={coupon} onChange={e => setCoupon(e.target.value)} placeholder="Slevovy kod"
                  className="flex-1 h-10 border border-slate-300 rounded px-3 text-sm focus:border-[#004E98] outline-none" data-testid="coupon-input" />
                <button type="button" onClick={applyCoupon} className="h-10 px-3 border border-[#004E98] text-[#004E98] rounded text-sm font-medium hover:bg-blue-50" data-testid="apply-coupon-btn">
                  Pouzit
                </button>
              </div>

              <div className="border-t border-slate-200 pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Mezisoucet s DPH:</span>
                  <span>{total.toFixed(2)} Kc</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Mezisoucet bez DPH:</span>
                  <span className="text-slate-400">{(total / 1.21).toFixed(2)} Kc</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">DPH 21%:</span>
                  <span className="text-slate-400">{(total - total / 1.21).toFixed(2)} Kc</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-sm text-red-600">
                    <span>Sleva:</span><span>-{discount.toFixed(2)} Kc</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Doprava:</span>
                  <span>{shippingCost === 0 ? <span className="text-emerald-600 font-medium">Zdarma</span> : `${shippingCost} Kc`}</span>
                </div>
                {codFee > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Dobirka:</span><span>{codFee} Kc</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold text-[#004E98] pt-3 border-t border-slate-200">
                  <span>Celkem:</span><span>{grandTotal.toFixed(2)} Kc</span>
                </div>
              </div>

              <button type="submit" disabled={submitting} className="w-full h-12 bg-[#FF6B35] text-white rounded font-bold mt-4 flex items-center justify-center gap-2 hover:bg-[#E85D2A] disabled:opacity-50 transition-colors" data-testid="submit-order-btn">
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
                Odeslat objednavku
              </button>
              <p className="text-xs text-slate-400 text-center mt-2">Odeslanim souhlasim s obchodnimi podminkami</p>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
